<template>
    <page
        :busy="busy"
        :tabs="tabsToRender"
        :can-add-tab="false"
        :can-remove-tab="false"
        :compact-mobile-view="true"
        class="i-calendar-page"
    >
        <b-col>
            <b-row class="action-buttons p-3">
                <form-checkbox
                    id="exclude-guest-name"
                    class="mr-4"
                    v-model="omit_guest_name_from_icalendar"
                    @change="onCheckBoxChange"
                >
                    Exclude guest name from iCal feed
                </form-checkbox>
                <form-checkbox
                    v-model="omit_property_from_icalendar"
                    id="exclude-property-name"
                    class="mr-4"
                    @change="onCheckBoxChange"
                >
                    Exclude property name from iCal feed
                </form-checkbox>
                <form-checkbox
                    name="icalendar_allow_old_url"
                    v-model="icalendar_allow_old_url"
                    id="allow-old-format"
                    @change="onCheckBoxChange"
                >
                    Allow old format iCal URL
                </form-checkbox>
            </b-row>
        </b-col>
        <grid ref="grid" :columnDefs="columnDefs" :row-data="urls" :is-auto-height="false">
            <div class="toolbar">
                <responsive-button
                    title="Import iCal - All Sources"
                    variant="primary"
                    :icon="['fas', 'right-left']"
                    @click="openSyncCalendarsModal"
                    >Import iCal - All Sources
                </responsive-button>
                <responsive-button
                    title="Refresh Table"
                    variant="primary"
                    :icon="['fas', 'arrows-rotate']"
                    @click="fetchCurrentData"
                    >Refresh
                </responsive-button>
            </div>
            <modal title="Sync All Calendars" id="sync-calendars" @ok="syncCalendars">
                Are you sure you want to fetch blocks from all external calendars? This task will run in the background
                and it may take up to 30 minutes to complete.
            </modal>
            <busy-modal :show="busy"></busy-modal> </grid
    ></page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Grid from '../generic/grid/grid.vue';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import ImportUrlsCellRenderer from './ical-import-urls-renderer.vue';
    import ExportUrlsCellRenderer from './ical-export-urls-renderer.vue';
    import ICalActionCellRenderer from './ical-actions-cell-renderer.vue';
    import FormCheckBox from '../generic/form-checkbox/form-checkbox.vue';
    import Modal from '../generic/modal/modal.vue';
    import Page from '../generic/page/page.vue';
    import { mapState } from 'vuex';
    import {
        ICALENDAR_SET_AUTO_IMPORT_ENDPOINT,
        ICALENDAR_SET_GLOBAL_SETTINGS_ENDPOINT,
        ICALENDAR_DATA_ENDPOINT,
    } from '../../endpoints';
    import API from '../../api';
    import BusyModal from '../generic/busy-modal/busy-modal.vue';
    import { WebSocketClient } from '../../websocket/client';
    import formCheckbox from '../generic/form-checkbox/form-checkbox.vue';

    export default {
        components: {
            Grid,
            ResponsiveButton,
            Modal,
            BusyModal,
            Page,
            FormCheckBox,
            importUrlsCellRenderer: ImportUrlsCellRenderer,
            exportUrlsCellRenderer: ExportUrlsCellRenderer,
            iCalActionCellRenderer: ICalActionCellRenderer,
            formCheckbox,
        },
        created() {
            this.fetchCurrentData();
        },
        data() {
            return {
                omit_guest_name_from_icalendar: null,
                omit_property_from_icalendar: null,
                icalendar_allow_old_url: null,
                tabsToRender: [
                    {
                        id: 'icalendar-urls',
                        title: 'ICalendar Setup',
                        active: true,
                        route: '/app/icalendar/urls',
                    },
                    {
                        id: 'icalendar-logs',
                        title: 'Logs',
                        active: false,
                        route: '/app/icalendar/logs',
                    },
                ],
                columnDefs: [
                    {
                        field: 'id',
                        headerName: 'ID',
                        type: 'idColumn',
                        maxWidth: 100,
                        cellStyle: { textAlign: 'center' },
                        cellClass: 'site-order',
                        sort: 'asc',
                    },
                    {
                        field: 'name',
                        headerName: 'Property',
                        minWidth: 200,
                    },
                    {
                        field: 'feed_url',
                        headerName: 'iCal Export URL(s)',
                        cellClass: 'export-urls',
                        cellRenderer: 'exportUrlsCellRenderer',
                        minWidth: 300,
                        maxWidth: 600,
                    },
                    {
                        field: 'ecallinks',
                        headerName: 'iCal Import URL(s)',
                        cellRenderer: 'importUrlsCellRenderer',
                        cellStyle: { padding: '10px' },
                        headerClass: 'ecallinks-header',
                        autoHeight: true,
                        minWidth: 300,
                        valueFormatter: ({ value }) => value,
                    },
                    {
                        field: 'icalendar_auto',
                        headerName: 'Auto Import?',
                        type: 'switchColumn',
                        headerClass: 'wordpress-enabled',
                        cellClass: 'wordpress-enabled',
                        maxWidth: 150,
                        minWidth: 150,
                        cellRendererParams: {
                            onChange: ({ node }, value) => {
                                this.toggleAutoImport({ id: node.id, value });
                            },
                        },
                    },
                    {
                        field: '',
                        cellClass: 'wordpress-enabled',
                        cellRenderer: 'iCalActionCellRenderer',
                        cellRendererParams: {
                            update: async () => {
                                await this.$store.dispatch('iCalendar/fetchUrls');
                            },
                        },
                        maxWidth: 150,
                        minWidth: 150,
                        resizable: false,
                    },
                ],
                busy: false,
                websocketClient: new WebSocketClient(),
            };
        },
        mounted() {
            this.fetchData();
        },
        computed: {
            ...mapGetters('header', ['userWebsocketToken']),
            ...mapState('iCalendar', ['urls']),
        },
        methods: {
            async fetchData() {
                return await Promise.all([this.fetchSubscSettings(), this.fetchCurrentData()]);
            },
            async onCheckBoxChange(value) {
                if (!this.busy) {
                    try {
                        this.busy = true;
                        await API.post(ICALENDAR_SET_GLOBAL_SETTINGS_ENDPOINT(), {
                            omit_guest_name_from_icalendar: this.omit_guest_name_from_icalendar,
                            omit_property_from_icalendar: this.omit_property_from_icalendar,
                            icalendar_allow_old_url: this.icalendar_allow_old_url,
                        });
                    } catch (e) {
                        this.errorToast({ text: `Could not update data.` });
                    } finally {
                        this.busy = false;
                    }
                }
            },
            async fetchSubscSettings() {
                try {
                    const response = await API.get(ICALENDAR_DATA_ENDPOINT());
                    this.omit_guest_name_from_icalendar =
                        response.data.subscriber_settings.omit_guest_name_from_icalendar;
                    this.omit_property_from_icalendar = response.data.subscriber_settings.omit_property_from_icalendar;
                    this.icalendar_allow_old_url = response.data.subscriber_settings.icalendar_allow_old_url;
                } catch (e) {
                    this.errorToast({ text: `Could not load subscriber settings.` });
                }
            },
            async fetchCurrentData() {
                try {
                    this.busy = true;
                    await this.$store.dispatch('iCalendar/fetchUrls');
                } catch (e) {
                    this.errorToast({ text: `Could not load calendar setup data.` });
                } finally {
                    this.busy = false;
                }
            },
            openSyncCalendarsModal() {
                this.$bvModal.show('sync-calendars');
            },
            async syncCalendars() {
                try {
                    await this.$store.dispatch('iCalendar/syncCalendar');
                    this.successToast({ text: 'Calendar(s) are now importing in the background ...' });
                } catch (e) {
                    this.errorToast({ text: `Could not sync calendars` });
                }
            },
            toggleAutoImport(data) {
                this.updateItemById('icalendar_auto', data, ICALENDAR_SET_AUTO_IMPORT_ENDPOINT(data.id));
            },
            async updateItemById(name, { id, value }, url) {
                try {
                    await API.post(url, { [name]: value });
                    await this.fetchCurrentData();
                } catch (err) {
                    this.errorToast({ text: `Could not update data.` });
                }
            },
            handleWebsocketMessage(message) {
                message.msg.indexOf('FINISHED') > -1
                    ? this.successToast({ text: message.msg.split(',')[1] })
                    : this.errorToast({ text: msg.msg.split(',')[1] });
            },
        },
        watch: {
            userWebsocketToken(newVal, oldVal) {
                this.$store.commit('iCalendar/setWebsocketToken', this.userWebsocketToken);
                this.websocketClient.initialize({ token: this.userWebsocketToken });
                this.websocketClient.setMessageCallback(this.handleWebsocketMessage);
            },
        },
    };
</script>

<style>
    .i-calendar-page {
        display: flex;
        flex-direction: column;
    }
</style>
